import { connect } from 'react-redux';
import { ComponentClass } from 'react';
import MatchList from '../../match-list';
import { AppState } from '@om-fe/core/core/types';
import { areLiveMatchListTopicESportPropsEqual, areLiveMatchListTopicPropsEqual, getLiveMatchListSliceName } from './live-match-list.utils';
import {
    addMoreLiveMatchList,
    getLiveMatchList,
    getLiveGroupsOverviewMatchList,
    removeLiveMatchList,
    removeLiveGroupsOverviewMatchList,
    updateLiveMatchList,
    updateLiveGroupsOverviewMatchList,
} from './live-match-list.actions';
import { LiveMatchListStoreModel } from './live-match-list.constants';
import { compose } from 'redux';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import { DEFAULT_DISPLAYED_NUMBER_OF_MATCHES, MatchListItemLayout } from '../../utils/match-list.constants';
import { MatchListCommon } from '../../utils/match-list-models';
import MatchListSkeleton from '../../match-list-skeleton';
import { SessionInfoModel, DisciplinesType } from '@om-fe/models';
import difference from 'lodash/difference';
import { FilteredMarketGroupsOverview } from '../../utils/match-list-models';
import { PlatformTypes } from '@om-fe/utils';

export interface LiveMatchListPublicProps extends MatchListCommon {
    readonly disciplineId: string;
    readonly locationId?: string;
    readonly isDefault?: boolean;
    readonly isLoadingData?: boolean;
    readonly eventCategoryId?: string;
    readonly itemLayout?: MatchListItemLayout;
    readonly groupOverview?: string;
    readonly hasMarketSelector?: boolean;
    readonly showMatchListHeader: boolean;
    readonly showMarketInMatchListGroupHeader: boolean;
    readonly selectGroupsOverview?: (item: string) => void;
    readonly filteredMarketGroupsOverviewList?: string[];
    readonly filteredMarketGroupsOverview?: Record<string, FilteredMarketGroupsOverview>;
    readonly isHasDrawer?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly preventRedirect?: boolean;
    readonly isAuthenForTopic?: boolean;
}

interface LiveMatchListStoreProps {
    readonly sliceKey: string;
    readonly limit: number;
    readonly totalRecords: number;
    readonly ready: boolean;
    readonly isAuthenticated: boolean;
}

interface LiveMatchListDispatchProps {
    readonly addMore: (instanceId: string, limitNumberOfEvents: number) => void;
}

const LiveMatchList: ComponentClass<LiveMatchListPublicProps> = compose<ComponentClass<LiveMatchListPublicProps>>(
    preLoader<LiveMatchListPublicProps>({
        preload({ props, dispatch }) {
            const { instanceId, disciplineId, locationId, isDefault, mainMarketsLimit, limitNumberOfEvents, eventCategoryId, groupOverview, hasMarketSelector, isAuthenForTopic } = props;

            const limit: number = limitNumberOfEvents || DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;
            if (hasMarketSelector && groupOverview) {
                dispatch(getLiveGroupsOverviewMatchList(instanceId, disciplineId, limit, locationId, isDefault, mainMarketsLimit, eventCategoryId, groupOverview, isAuthenForTopic));
            } else {
                dispatch(getLiveMatchList(instanceId, disciplineId, limit, locationId, isDefault, mainMarketsLimit, eventCategoryId, isAuthenForTopic));
            }
        },
        getReadySlice({ instanceId }: LiveMatchListPublicProps): string {
            const slice: string = getLiveMatchListSliceName(instanceId);

            return `${slice}.ready`;
        },
        update({ props, prevProps, dispatch }): void {
            const { instanceId, disciplineId, locationId, isDefault, mainMarketsLimit, limitNumberOfEvents, eventCategoryId, groupOverview, hasMarketSelector, isAuthenForTopic } = props;

            const limit: number = limitNumberOfEvents || DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;
            if (disciplineId === DisciplinesType.ElectronicSports) {
                if (hasMarketSelector) {
                    if ((prevProps.groupOverview !== groupOverview && prevProps.locationId === locationId) || isAuthenForTopic !== prevProps.isAuthenForTopic) {
                        dispatch(removeLiveGroupsOverviewMatchList(prevProps.instanceId, true));
                        dispatch(getLiveGroupsOverviewMatchList(instanceId, disciplineId, limit, locationId, isDefault, mainMarketsLimit, eventCategoryId, groupOverview, isAuthenForTopic));
                    } else if(!areLiveMatchListTopicESportPropsEqual(prevProps, props)) {
                        dispatch(updateLiveGroupsOverviewMatchList(instanceId, disciplineId, limit, locationId, isDefault, mainMarketsLimit, eventCategoryId, groupOverview, isAuthenForTopic));
                    }
                }
            } else {
                if (hasMarketSelector) {
                    if (groupOverview !== prevProps.groupOverview || isAuthenForTopic !== prevProps.isAuthenForTopic) {
                        if (groupOverview && prevProps.groupOverview && (difference(groupOverview.split(','), prevProps.groupOverview.split(',')).length != 0) || isAuthenForTopic !== prevProps.isAuthenForTopic) {
                            dispatch(removeLiveGroupsOverviewMatchList(prevProps.instanceId, true));
                        }
                        dispatch(getLiveGroupsOverviewMatchList(instanceId, disciplineId, limit, locationId, isDefault, mainMarketsLimit, eventCategoryId, groupOverview, isAuthenForTopic));
                    } else if (!areLiveMatchListTopicPropsEqual(prevProps, props)) {
                        dispatch(updateLiveGroupsOverviewMatchList(instanceId, disciplineId, limit, locationId, isDefault, mainMarketsLimit, eventCategoryId, groupOverview, isAuthenForTopic));
                    } 
                } else {
                    if (instanceId !== prevProps.instanceId || groupOverview !== prevProps.groupOverview || isAuthenForTopic !== prevProps.isAuthenForTopic) {
                        dispatch(removeLiveMatchList(prevProps.instanceId ));
                        dispatch(getLiveMatchList(instanceId, disciplineId, limit, locationId, isDefault, mainMarketsLimit, eventCategoryId, isAuthenForTopic));
                    } else if (!areLiveMatchListTopicPropsEqual(prevProps, props)) {
                        dispatch(updateLiveMatchList(instanceId, disciplineId, limit, locationId, isDefault, mainMarketsLimit, eventCategoryId, isAuthenForTopic));
                    }
                }
            }
        },
        destroy({ props, dispatch }) {
            const { instanceId, hasMarketSelector } = props;
            hasMarketSelector
            ? dispatch(removeLiveGroupsOverviewMatchList(instanceId, undefined))
            : dispatch(removeLiveMatchList(instanceId));
        },
        loadingAnimation: MatchListSkeleton,
    }),
    connect<LiveMatchListStoreProps,
        LiveMatchListDispatchProps,
        LiveMatchListPublicProps>(
        (state: AppState, props: LiveMatchListPublicProps): LiveMatchListStoreProps => {
            const { instanceId } = props;
            const sliceKey: string = getLiveMatchListSliceName(instanceId);
            const slice: LiveMatchListStoreModel | undefined = state[sliceKey];
            const sessionInfo: SessionInfoModel | undefined = state.session && state.session.info;
            const isAuthenticated: boolean = !!(sessionInfo && sessionInfo.isAuthenticated);

            if (!slice) {
                return {
                    sliceKey,
                    limit: 0,
                    totalRecords: 0,
                    ready: false,
                    isAuthenticated,
                };
            }

            return {
                sliceKey,
                limit: slice.limit,
                totalRecords: slice.totalRecords,
                ready: slice.ready,
                isAuthenticated,
            };
        },
        {
            addMore: addMoreLiveMatchList,
        },
    ),
)(MatchList);
export default LiveMatchList;
